/**
 * The application shell.
 *
 * **The chrome is shared and whole** (FiveOX ADR-0037). `<PlatformShell>` is
 * a top bar, a left sidebar and the content between them; this app renders it
 * and composes nothing of its own around it. In particular:
 *
 * - The nav bar carries **no app navigation**. It is suite chrome: brand, org,
 *   notifications, app-switcher waffle, account menu — and the one slot the
 *   ADR predicted, `search`, which holds ProDoc's **Go To** (`go-to.tsx`).
 * - ProDoc's navigation goes in the **sidebar, as data** — `nav.tsx`.
 * - The bell is the shell's, mounted with **our** authenticated fetcher, not a
 *   token: each app owns a different session store with its own idea of what a
 *   dead session means, and the bell must not become one more place that
 *   decides.
 * - **No router import inside the package.** `renderLink` wraps each item in
 *   react-router's `<NavLink>`; without one the sidebar renders plain anchors.
 *
 * The prototype's `prodoc-shell.tsx` is folded in here (plan §3): its
 * `useOrg()` survives as `components/org-context.tsx`, carrying the evaluation
 * instead of a role.
 *
 * **W22 (FR-59, D57): upload is a popup.** `<UploadProvider>` wraps the shell:
 * the rail's *Upload* (`renderNavItem`), Go To's and every screen's button open
 * the same popup, and the shell's org switch and sign-out come through the
 * provider, which asks first while a file is still uploading.
 *
 * **W25 (platform FR-24b, FR-24c, FR-60, D76): the rail is upstream now.** The
 * prototype's foldable rail was a local addition to its vendored shell; it went
 * into `@fiveox/shell` (fiveox #446) and is turned on here — `collapsible`
 * folds the rail to its icons above `lg`, remembered per browser, and the
 * drawer below it is never folded. The same PR lets a rail item hold the app's
 * own tree: **Documents' `panel` is the folder tree** (`<FolderMenuProvider>`,
 * which also mounts the folder dialogs once), and `drawerKey` is the query
 * string, so a folder chosen in the drawer on a phone closes it. That is why
 * `OrgProvider` and `StepUpProvider` now sit *around* the shell: the tree in
 * its rail shares, and sharing needs the members and the step-up.
 */

import { NoAccess, Notifications, PlatformShell, signOutEverywhere, useSwitcherApps } from '@fiveox/shell'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router'

import { StepUpProvider } from './components/StepUpDialog'
import { FolderMenuProvider } from './components/folder-menu'
import { GoTo } from './components/go-to'
import { OrgProvider } from './components/org-context'
import { UploadProvider } from './components/upload-provider'
import { DOCUMENT, useEvaluation } from './lib/access'
import { getOrgId, setOrgId } from './lib/api'
import {
  ACCESS_API,
  ACCOUNTS_URL,
  ADMIN_URL,
  APP_NAME,
  APP_SLUG,
  APPMGMT_API,
  AUTH_API,
  COMM_API,
  CONSOLE_URL,
  CRM_URL,
  HELP_URL,
  LAUNCHER_URL,
  STORE_URL,
  SUPPORT_URL,
} from './lib/config'
import { useMe } from './lib/use-me'
import { useOrgs } from './lib/use-orgs'
import { session } from './lib/session'
import { DOCUMENTS_ITEM, navFor, renderNavItem } from './nav'
import { Paywall } from './routes/Paywall'

export function App({ children }: { children: ReactNode }) {
  const current = session.useSession()
  const location = useLocation()
  const me = useMe()
  // Resolve the org *before* anything asks for permissions: every request to
  // prodoc-service carries it, and `useEvaluation` is one of them.
  const { orgs, selected, loading: orgsLoading, failed: orgsFailed } = useOrgs()
  const { evaluation, loading, can } = useEvaluation()

  // **The waffle is identity-shaped, not app-shaped** (ADR-0054): an internal
  // caller is offered the internal apps their platform-scoped assignments
  // grant; an external caller gets the customer estate. One resolver, all apps.
  //
  // **`appmgmtApiUrl` + `orgId` are what put the org's own products in it.**
  // The shell reads `GET /api/apps/me/apps?org=…`, which establishes
  // membership of the org before it answers, and renders only the rows the
  // server marks `launchable` — owned **and** with an address in this
  // environment. A switcher must not offer a door with no room behind it.
  //
  // `fetcher` rather than a bare token, for the reason the bell takes one: it
  // refreshes a spent access token and replays, and this app's session store is
  // the only thing that knows what a dead session means here.
  const switcherApps = useSwitcherApps({
    accessApiUrl: ACCESS_API,
    accessToken: current?.accessToken ?? null,
    staffUrls: { 'core-admin': ADMIN_URL, 'core-crm': CRM_URL, 'core-support': SUPPORT_URL },
    platformUrls: {
      accounts: ACCOUNTS_URL,
      console: CONSOLE_URL,
      store: STORE_URL,
      help: HELP_URL,
    },
    accountsUrl: ACCOUNTS_URL,
    appmgmtApiUrl: APPMGMT_API,
    // Null until `useOrgs` resolves, which is a real state on first paint. The
    // shell holds the empty list until then rather than reading the wrong org.
    orgId: selected?.id ?? null,
    fetcher: session.authed,
  })

  const signOut = () => {
    void signOutEverywhere({
      authApiUrl: AUTH_API,
      refreshToken: current?.refreshToken ?? null,
      clearLocal: session.clearSession,
    })
  }

  if (orgsLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading your organisations…</p>
      </div>
    )
  }

  // Org-service did not answer. **Not** the same as belonging to no org, and
  // the copy must not say it is — telling someone they have no organisation
  // when the service is merely down sends them to support with the wrong
  // question.
  if (orgsFailed) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="We can’t reach your organisations"
        message="The organisation service did not answer. This is usually brief — please try again in a moment."
      />
    )
  }

  // Genuinely a member of nothing. A real state for a brand-new account, and
  // the only way out is to create or join one — which is accounts' job, not
  // ours.
  if (!selected || !getOrgId()) {
    return (
      <NoAccess
        launcherUrl={LAUNCHER_URL}
        onSwitchAccount={signOut}
        title="You’re not in an organisation yet"
        message="ProDoc keeps documents per organisation — the company or practice that holds the subscription and the workspaces. Create one from your account, or accept a colleague's invitation, then come back."
      />
    )
  }

  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground text-sm">Checking your access…</p>
      </div>
    )
  }

  // An empty verb set is the single answer for every lapse — never bought,
  // subscription suspended, app killed, authority unreachable. Enforcing
  // `permissions` alone is entitlement-correct (guide §7.3), and the *reason*
  // is what lets the screen say why — and say that nothing stored has gone.
  if (!evaluation || Object.keys(evaluation.permissions).length === 0) {
    return (
      <Paywall
        reason={evaluation?.entitlementReason ?? 'authority_unreachable'}
        orgs={orgs}
        selectedOrgId={selected.id}
        onSelectOrg={setOrgId}
      />
    )
  }

  const userId = current?.userId ?? null

  return (
    <UploadProvider orgId={selected.id} orgs={orgs} userId={userId} canUpload={can(DOCUMENT, 'can_create')} onSwitchOrg={setOrgId} onSignOut={signOut}>
      {(upload) => (
        <OrgProvider value={{ org: selected, orgs, evaluation, can, userId, me, switchOrg: upload.switchOrg }}>
          {/* Step-up for the acts that cannot be taken back (D24 – D26): one dialog, asked for by any screen — and by the menu's share. */}
          <StepUpProvider>
            {/* W25 (FR-60): the folder tree under Documents, its state and its dialogs, mounted once. */}
            <FolderMenuProvider>
              {(folders) => (
                <PlatformShell
                  // W25 (FR-24b): the rail folds to its icons above `lg`; the reader's choice is kept per browser.
                  collapsible
                  appName={APP_NAME}
                  appSlug={APP_SLUG}
                  launcherUrl={LAUNCHER_URL}
                  account={{
                    name: me?.name ?? null,
                    email: me?.email ?? null,
                    pictureUrl: me?.pictureUrl ?? null,
                    accountsUrl: ACCOUNTS_URL,
                    // W22 (FR-59): asks first while a file is still uploading.
                    onSignOut: upload.signOut,
                  }}
                  apps={switcherApps}
                  // Display-only, and the bar's alone (ADR-0062 §4): a staff account holds
                  // no organisation, so the bar drops the org control for one rather than
                  // each host deciding. Never a permission input — that is `useEvaluation`.
                  accessToken={current?.accessToken ?? null}
                  // The org switcher. One prop, two behaviours: with `options` and
                  // `onSelect` it switches; with only `selected` it renders a read-only
                  // chip — which is what a single-org user gets, correctly. The switch asks
                  // first while a file is still uploading (W22, FR-59).
                  org={{
                    selected: { id: selected.id, display_name: selected.display_name, role: selected.role },
                    options: orgs.length > 1 ? orgs : undefined,
                    onSelect: orgs.length > 1 ? upload.switchOrg : undefined,
                  }}
                  search={
                    <GoTo
                      userId={userId}
                      org={selected}
                      orgs={orgs}
                      can={can}
                      onSwitchOrg={upload.switchOrg}
                      onUpload={upload.canUpload ? () => upload.openUpload() : undefined}
                    />
                  }
                  notifications={<Notifications commApiUrl={COMM_API} fetcher={session.authed} />}
                  nav={{
                    // W25 (FR-24c, FR-60): Documents holds the folder tree; `null` for a person who may not see folders.
                    items: navFor(can, { [DOCUMENTS_ITEM]: folders.panel }),
                    currentPath: location.pathname,
                    // A folder chosen in the drawer changes the query string, not the path: this closes the drawer on it.
                    drawerKey: location.search,
                    // Every item a `<NavLink>`, and *Upload* a button that opens the popup (FR-59).
                    renderLink: renderNavItem(() => upload.openUpload()),
                  }}
                  // ProDoc's register is a wide table, so the content is capped at 1536px
                  // rather than the default 6xl — the prototype's choice, kept.
                  contentClassName="mx-auto w-full max-w-screen-2xl px-4 py-8 sm:px-6"
                >
                  {children}
                </PlatformShell>
              )}
            </FolderMenuProvider>
          </StepUpProvider>
        </OrgProvider>
      )}
    </UploadProvider>
  )
}
